import type { ReactNode } from 'react';

type Variant = 'info' | 'warn' | 'error' | 'ok';

// Un color de estado por variante, tomado del token del TEMA. Antes eran hexes fijos
// pensados para fondo oscuro (#e3b341 y compañía): heredados de cuando la app sólo tenía
// tema oscuro. Con el claro de por medio esos avisos quedaban entre 1.72:1 y 2.15:1 sobre
// blanco — incluido el "Caja cerrada" del punto de venta. Los tokens de estado sí cambian
// por tema, así que el aviso se lee en los dos.
const STATE: Record<Variant, string> = {
  info: 'var(--lumal-info)',
  warn: 'var(--lumal-warn)',
  error: 'var(--lumal-danger)',
  ok: 'var(--lumal-success)',
};

export interface BannerProps {
  variant?: Variant;
  children: ReactNode;
  testId?: string;
}

export function Banner({ variant = 'info', children, testId }: BannerProps) {
  const c = STATE[variant];
  return (
    <div
      data-testid={testId ?? `banner-${variant}`}
      // Sólo el error se anuncia: los avisos permanentes (el "Caja cerrada" del POS y demás
      // banners de estado) viven fijos en la pantalla, y con role="alert" el lector los
      // repetiría en cada repintado.
      role={variant === 'error' ? 'alert' : undefined}
      style={{
        background: `color-mix(in srgb, ${c} 13%, var(--lumal-surface))`,
        // El texto va a contraste PLENO, no en el color de estado: teñir el fondo y además
        // pintar el texto del mismo tono deja el aviso entre 4.1:1 y 4.7:1 según variante y
        // tema. El color de estado se comunica por el tinte y por la barra lateral, que son
        // superficie (3:1) y no texto (4.5:1).
        color: 'var(--lumal-fg)',
        border: `1px solid color-mix(in srgb, ${c} 38%, transparent)`,
        borderLeft: `4px solid ${c}`,
        borderRadius: 8,
        padding: '0.7rem 1rem',
        margin: '0.5rem 0',
        fontSize: '0.92rem',
      }}
    >
      {children}
    </div>
  );
}
